import classnames from 'classnames'
import { useEffect, useRef } from 'react'
import useKeyboardAccessibleGroup from 'marketing-site/lib/useKeyboardAccessibleGroup'
import { CTALink } from 'marketing-site/src/library/elements/CTALink'
import { ImageOrAutoplayVideo } from 'marketing-site/src/library/elements/ImageOrAutoplayVideo'
import { Text } from 'marketing-site/src/library/elements/Text'
import { WhatsIncludedDrawer } from 'marketing-site/src/library/elements/WhatsIncludedDrawer/'
import { BrandOptions, useBrand } from '../../providers/brandProvider'
import { WhatsIncluded as WhatsIncludedDefault } from './component'
import {
  containerMaxWidth,
  googleTagManagerCustomEvents,
  IWhatsIncludedDrawerClickedMetadata,
  mq,
  TIMING,
  triggerGoogleTagManagerCustomEvent,
} from 'marketing-site/src/library/utils'
import React, { useState } from 'react'
import { IProps } from './index'
import classNames from 'classnames/bind'

export const WhatsIncluded = (props: IProps) => {
  const theme = useBrand()
  if (theme === BrandOptions.default) {
    return <WhatsIncludedDefault {...props} />
  }
  return <WhatsIncludedLegacy {...props} />
}

const WhatsIncludedLegacy = ({
  bodyCopy,
  headline,
  slides,
  bgColor,
  ctaLink,
  leftAlignImage,
}: IProps) => {
  const [openSlideIndex, setOpenSlideIndex] = useState(0)
  const activeDrawer = useRef<HTMLLIElement>(null)
  const [previousHeight, setPreviousHeight] = useState<string>('unset')

  const { containerProps, itemProps } = useKeyboardAccessibleGroup<HTMLButtonElement>()

  function handleDrawerButtonClick(index: number) {
    setOpenSlideIndex(index)
    const metadata: IWhatsIncludedDrawerClickedMetadata = {
      'whats-included-drawer-index': index,
      'whats-included-drawer-title': slides[index].title,
      'whats-included-component-headline': headline,
    }
    triggerGoogleTagManagerCustomEvent(
      googleTagManagerCustomEvents.whatsIncludedDrawerClicked,
      metadata,
    )
  }

  useEffect(() => {
    const previousActiveLi = activeDrawer.current
    if (!previousActiveLi) {
      return
    }
    const previousHeight = `${previousActiveLi.getBoundingClientRect().height}px`

    setPreviousHeight(previousHeight)
  }, [openSlideIndex])

  useEffect(() => {
    const activeLi = activeDrawer.current
    if (!activeLi) {
      setPreviousHeight('unset')
      return
    }
    const content = activeLi.firstElementChild
    if (!content) {
      setPreviousHeight('unset')
      return
    }
    let text = ''
    // @ts-ignore
    text = content.innerText || text

    if (!text.trim()) {
      setPreviousHeight('unset')
      return
    }

    const newHeight = `${content.getBoundingClientRect().height + 38}px`
    setPreviousHeight(newHeight)
  }, [openSlideIndex])

  const isActive = (index: number) => {
    return openSlideIndex === index
  }

  const whatsIncludedClassNames = classnames('whats-included', {
    'whats-included-left-align-image': leftAlignImage,
  })

  return (
    <div className={whatsIncludedClassNames}>
      <div className="main-info-column">
        <h1 className="headline">
          <Text size="xxl+">{headline}</Text>
        </h1>
        {bodyCopy && (
          <p className="body-copy">
            <Text size="body">{bodyCopy}</Text>
          </p>
        )}
        <ol className="drawers" {...containerProps}>
          {slides.map((slide, index) => {
            return (
              <li
                ref={isActive(index) ? activeDrawer : undefined}
                className={classnames('drawer-item', { 'drawer-item--active': isActive(index) })}
                key={index}
              >
                <button
                  className="drawer-button"
                  type="button"
                  {...itemProps(index)}
                  aria-expanded={isActive(index)}
                  onClick={() => handleDrawerButtonClick(index)}
                >
                  <WhatsIncludedDrawer {...slide} theme={bgColor} isOpen={isActive(index)} />
                </button>
              </li>
            )
          })}
        </ol>
        {ctaLink && (
          <div className="cta">
            <CTALink {...ctaLink} />
          </div>
        )}
      </div>
      <div className="slide-image -active">
        {slides.map((slide, index) => {
          slide.imageRef.borderRadius = 25
          const selectedSlide = classNames('selected-slide', {
            'selected-slide-on': isActive(index),
            '-active': isActive(index),
          })
          return (
            <div className={selectedSlide} key={slide.title}>
              <ImageOrAutoplayVideo
                imageRef={slide.imageRef}
                video={slide.video}
                videoWebm={slide.videoWebm}
              />
            </div>
          )
        })}
      </div>
      <style jsx>
        {`
          .whats-included {
            display: grid;
            grid-template-columns: [left-gutter] 1fr [content] 12fr [right-gutter] 1fr [image-content];
            grid-row-gap: 1rem;
            margin: 0 auto;
            max-width: ${containerMaxWidth};
          }

          .main-info-column {
            grid-column: content;
          }

          .headline {
            text-align: center;
            margin-bottom: 24px;
          }

          .body-copy {
            margin-bottom: 36px;
            text-align: center;
          }

          .drawers {
            padding: 0;
            margin: 0;
          }

          .drawer-item {
            backface-visibility: hidden;
            user-select: text;

            &--active {
              height: ${previousHeight};
              overflow: hidden;
              transition: height ${TIMING.STANDARD}ms;
            }
          }

          .drawer-button {
            border-radius: 6px;
            transition: background-color ${TIMING.STANDARD}ms;
            margin: 3px 0;
            min-width: 100%;
          }

          .drawer-button:hover,
          .drawer-button:focus {
            outline: none;
          }

          .slide-image {
            display: grid;
            grid-column: 2;
            grid-template-columns: 1fr;
            margin: auto;
            order: ${leftAlignImage && 1};
            border-radius: 25px;
            overflow: hidden;
          }

          .selected-slide {
            opacity: 0;
            grid-row-start: 1;
            grid-column-start: 1;
            &-on {
              opacity: 1;
            }
          }

          .cta {
            padding: 24px;
          }

          @media (${mq.tablet}) {
            .whats-included {
              grid-template-columns: [left-gutter] 3fr [content] 8fr [right-gutter] 3fr;
            }
          }

          @media (${mq.laptop}) {
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }

            .-active {
              animation: fadeIn ${TIMING.STANDARD}ms;
            }

            .whats-included {
              grid-template-columns:
                [left-gutter] 1fr [drawer-content] 6fr
                [middle-gutter] 1fr [image-content] 6fr [right-gutter] 1fr;
            }
            .whats-included-left-align-image {
              grid-template-columns:
                [left-gutter] 1fr [image-content] 6fr
                [middle-gutter] 1fr [drawer-content] 6fr [right-gutter] 1fr;
            }

            .slide-image {
              grid-column: image-content;
            }

            .headline {
              text-align: left;
              margin-bottom: 24px;
            }

            .body-copy {
              margin-bottom: 36px;
              text-align: left;
            }

            .main-info-column {
              grid-column: drawer-content;
              order: ${leftAlignImage && 2};
            }
          }

          @media (${mq.desktopLg}) {
            .whats-included {
              grid-template-columns:
                [left-gutter] 1fr [drawer-content] 5fr
                [middle-gutter] 1fr [image-content] 7fr [right-gutter] 1fr;
            }
            .whats-included-left-align-image {
              grid-template-columns:
                [left-gutter] 1fr [image-content] 7fr
                [middle-gutter] 1fr [drawer-content] 5fr [right-gutter] 1fr;
            }
          }
        `}
      </style>
    </div>
  )
}
